import { useNavigate } from "react-router";
import { useTranslation } from "@localization/useTranslation";
import { useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { getUniqueFilterOptions, getUniqueNestedFilterOptions, sortByEnumValue, sortByName } from "@common/hooks/filterUtils.ts";
import { setFirstLetterToLowerCase } from "@common/stringOperations.ts";
import { SystemStatus } from "../../../system/systemEdit/SystemEdit.types";
import { SelectableColoredItem } from "../../ProcessingActivity.types";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../../useUrlProvider";
import { DistributionArea } from "../../systemsStep/SystemsStep.types";
import { IProcessingActivityAssetsTab } from "./ProcessingActivityAssetsTab";

interface ProcessingActivityAssetViewModel {
    id: string;
    systemName: string;
    distributionArea: DistributionArea;
    businessAreas: Array<SelectableColoredItem>;
    responsible: string | null;
    status: SystemStatus;
}

export interface ProcessingActivityAssetTableRow {
    id: string;
    name: string;
    typeId: string;
    type: string;
    businessAreas: Array<SelectableColoredItem>;
    businessAreaNames: string;
    responsible: string;
    statusId: SystemStatus;
    status: string;
}

export default function useProcessingActivityAssetsTab(props: IProcessingActivityAssetsTab) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { getSystemOverviewUrl } = useUrlProvider();
    const navigate = useNavigate();

    const { isLoading, data } = useDotLegalQueryWithKey<Array<ProcessingActivityAssetViewModel>>(
        "processingOverviewSystemTable" + props.processingActivityId,
        "/processingactivityoverview/systems/" + props.processingActivityId
    );

    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `processingActivity-${props.processingActivityId}-assets-filters`
    );

    const allRows: Array<ProcessingActivityAssetTableRow> = (data ?? []).map((asset) => ({
        id: asset.id,
        name: asset.systemName,
        typeId: asset.distributionArea.toString(),
        type: translateString(DistributionArea[asset.distributionArea].toLowerCase()),
        businessAreas: asset.businessAreas,
        businessAreaNames: asset.businessAreas.map((businessArea) => businessArea.name).join(", "),
        responsible: asset.responsible ?? "",
        statusId: asset.status,
        status: translateString(setFirstLetterToLowerCase(SystemStatus[asset.status])),
    }));

    const assets = applyClientFilters(
        allRows,
        [
            { id: "asset", candidate: (row) => row.id },
            { id: "type", candidate: (row) => row.typeId },
            { id: "businessArea", candidate: (row) => row.businessAreas.map((businessArea) => businessArea.id) },
            { id: "responsible", candidate: (row) => row.responsible },
            { id: "status", candidate: (row) => row.statusId.toString() },
        ],
        selectFilters,
        {},
        filterOperators
    );

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "asset",
                    label: translateString("asset"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.asset ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows,
                        (row) => row.name,
                        (row) => row.id
                    ).sort(sortByName),
                },
                {
                    id: "type",
                    label: translateString("type"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.type ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows,
                        (row) => row.type,
                        (row) => row.typeId
                    ).sort(sortByEnumValue),
                },
                {
                    id: "businessArea",
                    label: translateString("businessAreas"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.businessArea ?? [],
                    fixedData: getUniqueNestedFilterOptions(
                        allRows,
                        (row) => row.businessAreas,
                        (businessArea) => businessArea.name,
                        (businessArea) => businessArea.id
                    ).sort(sortByName),
                },
                {
                    id: "responsible",
                    label: translateString("responsible"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.responsible ?? [],
                    fixedData: getUniqueFilterOptions(allRows, (row) => row.responsible).sort(sortByName),
                },
                {
                    id: "status",
                    label: translateString("status"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.status ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows,
                        (row) => row.status,
                        (row) => row.statusId.toString()
                    ).sort(sortByEnumValue),
                },
            ],
            filterOperators
        );
    }

    const canOpenAsset = permissions.systemPermissions.read;
    const hasActiveFilters = Object.values(selectFilters).some((v) => v.length > 0);

    return {
        isLoading,
        assets: assets ?? [],
        hasData: isLoading || allRows.length > 0,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        canOpenAsset,
        getAssetUrl: getSystemOverviewUrl,
        openAsset: (assetId: string) => navigate(getSystemOverviewUrl(assetId)),
    };
}
